/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { defineAppHotkeys, type Key } from '@tanstack/react-hotkeys';
import { isMacPlatform } from '../../agent/keyboard';

// Every declared desktop shortcut, keyed by a stable id (saved custom shortcuts use the ids).
// Declarations only: callbacks are attached where each action lives, with `useHotkey`.

/** Scope of the Agent's shortcuts. App windows nest inside it, so the focused window wins. */
export const AGENT_HOTKEY_SCOPE = 'agent';

const isMac = isMacPlatform();

/** The Agent's modifier: ⌃ on macOS, Alt elsewhere (see `agent/keyboard.ts`). */
const agentChord = <K extends Key>(key: K) =>
  isMac ? (`Control+${key}` as const) : (`Alt+${key}` as const);

const agentHotkey = { scope: AGENT_HOTKEY_SCOPE, options: { ignoreInputs: false } } as const;

export const appHotkeys = defineAppHotkeys({
  // Global: works from any window, the Agent and Quick Find.
  'settings.open': {
    hotkey: 'Mod+,',
    scope: 'global',
    meta: { name: 'Open Settings', group: 'General' },
  },
  // ⌃Space reaches the Agent from a field too: it focuses the composer, or opens Quick Find while
  // the Agent is closed. That leaves ⌘K free for apps' own search.
  'agent.focus': {
    ...agentHotkey,
    hotkey: agentChord('Space'),
    meta: { name: 'Ask the Agent', group: 'Agent' },
  },
  // Opens and closes the Agent from anywhere, text fields included.
  'agent.toggle': {
    ...agentHotkey,
    hotkey: agentChord('A'),
    meta: { name: 'Show or hide the Agent', group: 'Agent' },
    options: { ignoreInputs: false, requireReset: true },
  },
  'agent.newConversation': {
    ...agentHotkey,
    hotkey: agentChord('N'),
    meta: { name: 'New conversation', group: 'Agent' },
  },
  'agent.searchHistory': {
    ...agentHotkey,
    hotkey: agentChord('F'),
    meta: { name: 'Search conversations', group: 'Agent' },
  },
  'agent.toggleHistory': {
    hotkey: agentChord('H'),
    scope: AGENT_HOTKEY_SCOPE,
    meta: { name: 'Show conversation history', group: 'Agent' },
    options: { ignoreInputs: false, requireReset: true },
  },
  // ⌃W (Alt+W elsewhere) closes the focused window's tab, else the window; the Agent leaves it to
  // apps. ⌘W and Windows' Ctrl+W belong to the browser.
  'window.close': {
    hotkey: agentChord('W'),
    meta: { name: 'Close tab or window', group: 'Desktop' },
    options: { ignoreInputs: false },
  },
  // Apps use ⌘K for their own search; Quick Find is on ⌃Space.
  'explorer.focusSearch': { hotkey: 'Mod+K', meta: { name: 'Search', group: 'Database Finder' } },
  // Browsers keep ⌘T, so the Finder takes ⌥⌘T (plain ⌘T is registered too, where it gets through).
  'explorer.newTab': { hotkey: 'Mod+Alt+T', meta: { name: 'New tab', group: 'Database Finder' } },
  'explorer.closeTab': {
    hotkey: agentChord('W'),
    meta: { name: 'Close tab', group: 'Database Finder' },
    options: { ignoreInputs: false },
  },
});

declare module '@tanstack/react-hotkeys' {
  interface Register {
    appHotkeys: typeof appHotkeys;
  }
}
